import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient, { ApiResponse, PaginationMeta } from "./apiClient";

export interface Campaign {
  id: string;
  name: string;
  description: string;
  start_date: string;
  expiry_date: string;
  image_url: string;
  is_enabled: boolean;
  is_active: boolean;
}

export interface CampaignPayload {
  name: string;
  description: string;
  start_date: string;
  expiry_date: string;
  image_url: string;
  is_enabled?: boolean;
}

export interface PaginatedCampaigns {
  campaigns: Campaign[];
  pagination: PaginationMeta;
}

const QUERY_KEY = ["campaigns"];

const fetchCampaigns = async (page: number): Promise<PaginatedCampaigns> => {
  const { data: response } = await apiClient.get<ApiResponse<{ campaigns: Campaign[]; pagination: PaginationMeta }>>(
    "/v1/admin/campaigns",
    { params: { page } }
  );
  return response.data;
};

const addCampaign = async (body: CampaignPayload): Promise<Campaign> => {
  const { data: response } = await apiClient.post<ApiResponse<Campaign>>("/v1/admin/campaigns", body);
  return response.data;
};

const updateCampaign = async ({ id, body }: { id: string; body: CampaignPayload }): Promise<Campaign> => {
  const { data: response } = await apiClient.put<ApiResponse<Campaign>>(`/v1/admin/campaigns/${id}`, body);
  return response.data;
};

const deleteCampaign = async (id: string): Promise<void> => {
  await apiClient.delete(`/v1/admin/campaigns/${id}`);
};


export const useCampaigns = (page: number = 1) =>
  useQuery({
    queryKey: [...QUERY_KEY, page],
    queryFn: () => fetchCampaigns(page),
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useAddCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};


// ── Campaign Broadcasts ───────────────────────────────────────────────────────

export type CampaignBroadcastStatus = "pending" | "processing" | "completed" | "failed" | "cancelled";

export interface CampaignBroadcast {
  broadcast_id: number;
  broadcast_number: number;
  status: CampaignBroadcastStatus;
  source_type: string;
  source_id: number;
  total_recipients: number;
  sent_count: number;
  skipped_count: number;
  failed_count: number;
  progress_percent: number;
  triggered_by_uid: string;
  created_at: string;
  started_at: string;
  completed_at: string;
  last_error: string;
}

const fetchCampaignBroadcasts = async (campaignId: string): Promise<CampaignBroadcast[]> => {
  const { data: response } = await apiClient.get<ApiResponse<CampaignBroadcast[]>>(
    `/v1/admin/campaigns/${campaignId}/broadcasts`
  );
  return response.data;
};

export const useCampaignBroadcasts = (campaignId: string) =>
  useQuery({
    queryKey: ["campaigns", campaignId, "broadcasts", "list"],
    queryFn: () => fetchCampaignBroadcasts(campaignId),
    enabled: !!campaignId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

const fetchCampaignBroadcast = async (campaignId: string, broadcastId: number): Promise<CampaignBroadcast> => {
  const { data: response } = await apiClient.get<ApiResponse<CampaignBroadcast>>(
    `/v1/admin/campaigns/${campaignId}/broadcasts/${broadcastId}`
  );
  return response.data;
};

export const useCampaignBroadcast = (campaignId: string, broadcastId: number, options?: { polling?: boolean }) =>
  useQuery({
    queryKey: ["campaigns", campaignId, "broadcasts", broadcastId],
    queryFn: () => fetchCampaignBroadcast(campaignId, broadcastId),
    enabled: !!campaignId && !!broadcastId,
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    refetchInterval: options?.polling ? 30000 : false,
  });

const triggerCampaignBroadcast = async (campaignId: string): Promise<CampaignBroadcast> => {
  const { data: response } = await apiClient.post<ApiResponse<CampaignBroadcast>>(
    `/v1/admin/campaigns/${campaignId}/broadcasts`
  );
  return response.data;
};

export const useTriggerCampaignBroadcast = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: triggerCampaignBroadcast,
    onSuccess: (_, campaignId) =>
      queryClient.invalidateQueries({ queryKey: ["campaigns", campaignId, "broadcasts"] }),
  });
};

